import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@/pages/home/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Footer from "@/pages/home/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/hooks/useAuth";
import { PASSWORD_MAX, PASSWORD_MIN, passwordProblem } from "@/lib/passwordPolicy";

const TOKEN = /^[A-Za-z0-9_-]{20,128}$/;

export default function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const { resetPassword } = useAuth();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(TOKEN.test(token));
  const [linkOk, setLinkOk] = useState(false);
  const [minutes, setMinutes] = useState(60);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!TOKEN.test(token)) {
      setChecking(false);
      setLinkOk(false);
      return;
    }
    let mounted = true;
    api
      .post<{ ok: true; expiresInMinutes: number }>("/auth/reset-password/status", { token })
      .then((result) => {
        if (!mounted) return;
        setLinkOk(true);
        setMinutes(result.expiresInMinutes);
      })
      .catch(() => {
        if (!mounted) return;
        setLinkOk(false);
      })
      .finally(() => {
        if (mounted) setChecking(false);
      });
    return () => {
      mounted = false;
    };
  }, [token]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError(t("auth.reset.mismatch"));
      return;
    }
    const problem = passwordProblem(password);
    if (problem) {
      setError(t(`auth.password.problems.${problem}`, { min: PASSWORD_MIN.user, max: PASSWORD_MAX }));
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, password);
      navigate("/profile", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.errors.generic"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="min-h-screen relative flex items-center justify-center overflow-hidden bg-primary-950 px-4 md:px-6 py-10">
        <div className="absolute inset-0">
          <div className="absolute -top-32 -left-24 w-[480px] h-[480px] rounded-full bg-accent-400/20 blur-3xl"></div>
          <div className="absolute bottom-0 -right-24 w-[420px] h-[420px] rounded-full bg-primary-400/20 blur-3xl"></div>
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="flex justify-end mb-4">
            <LanguageSwitcher dark />
          </div>
          <div className="flex justify-center mb-8">
            <Logo dark />
          </div>

          <div className="glass-dark rounded-2xl p-6 md:p-8">
            {checking ? (
              <div className="flex justify-center py-8">
                <div className="w-10 h-10 rounded-full loader-ring"></div>
              </div>
            ) : linkOk ? (
              <>
                <h1 className="font-heading font-bold text-2xl text-background-50">
                  {t("auth.reset.title")}
                </h1>
                <p className="mt-1 text-sm text-background-50/70">
                  {t("auth.reset.subtitle", { minutes })}
                </p>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-background-50/80 mb-1.5">
                      {t("auth.fields.password")}
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      minLength={PASSWORD_MIN.user}
                      maxLength={PASSWORD_MAX}
                      aria-describedby="password-rules"
                      className="w-full rounded-lg bg-background-50/10 border border-background-50/15 px-4 py-3 text-sm text-background-50 placeholder:text-background-50/40 focus:outline-none focus:ring-2 focus:ring-accent-400"
                    />
                    <ul id="password-rules" className="mt-2 space-y-1 text-xs" aria-live="polite">
                      {[
                        { key: "length", ok: password.length >= PASSWORD_MIN.user },
                        { key: "letter", ok: /\p{L}/u.test(password) },
                        { key: "number", ok: /\p{N}/u.test(password) },
                        { key: "personal", ok: true },
                      ].map(({ key, ok }) => (
                        <li
                          key={key}
                          className={`flex items-center gap-1.5 ${
                            key === "personal" ? "text-background-50/50" : ok ? "text-accent-200" : "text-background-50/60"
                          }`}
                        >
                          <i
                            className={
                              key === "personal"
                                ? "ri-information-line"
                                : ok
                                  ? "ri-checkbox-circle-fill"
                                  : "ri-checkbox-blank-circle-line"
                            }
                            aria-hidden="true"
                          ></i>
                          {t(`auth.password.rules.${key}`, { min: PASSWORD_MIN.user })}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div>
                    <label htmlFor="confirm" className="block text-sm font-medium text-background-50/80 mb-1.5">
                      {t("auth.fields.confirmPassword")}
                    </label>
                    <input
                      id="confirm"
                      name="confirm"
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      placeholder="••••••••"
                      autoComplete="new-password"
                      required
                      minLength={PASSWORD_MIN.user}
                      maxLength={PASSWORD_MAX}
                      className="w-full rounded-lg bg-background-50/10 border border-background-50/15 px-4 py-3 text-sm text-background-50 placeholder:text-background-50/40 focus:outline-none focus:ring-2 focus:ring-accent-400"
                    />
                  </div>

                  {error && (
                    <div className="rounded-lg bg-red-500/10 border border-red-400/30 px-4 py-3 text-sm text-red-300">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full whitespace-nowrap rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-3.5 text-sm font-semibold text-background-50 transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                  >
                    {loading ? t("common.actions.pleaseWait") : t("auth.reset.submit")}
                  </button>
                </form>
              </>
            ) : (
              <>
                <h1 className="font-heading font-bold text-2xl text-background-50">
                  {t("auth.reset.title")}
                </h1>
                <p className="mt-3 text-sm text-background-50/70">
                  {TOKEN.test(token) ? t("auth.reset.expired") : t("auth.reset.missing")}
                </p>
                <Link
                  to="/auth"
                  state={{ forgot: true }}
                  className="mt-6 inline-flex whitespace-nowrap rounded-lg bg-gradient-to-r from-primary-500 to-accent-500 px-5 py-3.5 text-sm font-semibold text-background-50"
                >
                  {t("auth.reset.requestNew")}
                </Link>
              </>
            )}
          </div>

          <p className="mt-6 text-center text-sm text-background-50/60">
            <Link to="/" className="hover:text-background-50 transition-colors">
              ← {t("common.actions.backToHome")}
            </Link>
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
}
